import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { airports } from "@/data/airports";
import {
  getTerminalInfo,
  getTerminalGroup,
  getTerminalGroups,
} from "@/data/airlineTerminals";
import { airportDetails } from "@/data/airportDetails";
import { getDiningInfo } from "@/data/airportDining";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, MapPin, ShieldCheck, Utensils } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import {
  SchemaMarkup,
  generateBreadcrumbSchema,
  generateFAQSchema,
} from "@/components/SchemaMarkup";
import { LiveWaitStrip } from "@/components/airport/LiveWaitStrip";
import { AppStoreCTA } from "@/components/AppStoreCTA";
import NotFound from "./NotFound";

/**
 * One page per numbered terminal, e.g. /airports/jfk/terminals/terminal-8.
 *
 * These target the highest-volume searches in the category ("jfk terminal 8",
 * "o'hare terminal 1", "lga terminal b") and win the click by showing the
 * live security wait rather than static reference text.
 */
const TerminalDetail = () => {
  const { code, airline: slug } = useParams<{ code: string; airline: string }>();
  const upper = code?.toUpperCase() ?? "";
  const lower = code?.toLowerCase() ?? "";

  const airport = airports.find((a) => a.code === upper);
  const info = getTerminalInfo(upper);
  const group = getTerminalGroup(upper, slug);

  if (!airport || !info || !group) return <NotFound />;

  const cityName = airport.city.split(",")[0];
  const url = `https://airportbuddy.app/airports/${lower}/terminals/${group.slug}`;
  const details = airportDetails[upper];
  const dining = getDiningInfo(upper);

  const otherTerminals = getTerminalGroups(upper).filter((g) => g.slug !== group.slug);

  const terminalVenues =
    dining?.venues.filter((v) => {
      const label = group.label.toLowerCase();
      const venue = v.terminal.toLowerCase();
      return venue === label || venue.includes(label) || label.includes(venue);
    }) ?? [];

  const airlineNames = group.airlines.map((a) => a.short);
  const airlineList =
    airlineNames.length > 1
      ? `${airlineNames.slice(0, -1).join(", ")} and ${airlineNames[airlineNames.length - 1]}`
      : airlineNames[0];

  const faqs = [
    {
      question: `Which airlines use ${group.label} at ${upper}?`,
      answer: `${airlineList} check in at ${group.label} at ${airport.name}. Terminal assignments do change, so confirm against your boarding pass on the day you fly.`,
    },
    {
      question: `How long is security at ${upper} ${group.label}?`,
      answer: `Airport Buddy shows the live TSA wait for ${upper} on this page, updated through the day. Standard lines at ${upper} average ${details?.avgDomesticWait ?? "15-25 minutes"} for domestic departures.`,
    },
    {
      question: `Can I get to another terminal from ${group.label}?`,
      answer: info.connection,
    },
    ...(terminalVenues.length
      ? [
          {
            question: `Where can I eat in ${group.label} at ${upper}?`,
            answer: `${group.label} has ${terminalVenues.length} tracked place${terminalVenues.length === 1 ? "" : "s"} to eat, including ${terminalVenues.slice(0, 3).map((v) => v.name).join(", ")}.`,
          },
        ]
      : []),
  ];

  return (
    <>
      <Helmet>
        <title>{`${upper} ${group.label} – Airlines, Security Wait & Map (${cityName})`}</title>
        <meta
          name="description"
          content={`${group.label} at ${airport.name} (${upper}): ${airlineList} fly from here. See the live TSA security wait, PreCheck lanes, food and how to reach other terminals.`}
        />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`${upper} ${group.label} – Airlines & Live Security Wait`} />
        <meta
          property="og:description"
          content={`Who flies from ${group.label} at ${upper}, plus the security wait right now.`}
        />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${upper} ${group.label}`} />
        <meta
          name="twitter:description"
          content={`Airlines, live security wait and food at ${upper} ${group.label}.`}
        />
      </Helmet>

      <SchemaMarkup
        schema={generateBreadcrumbSchema([
          { name: "Home", url: "/" },
          { name: "Airports", url: "/airports" },
          { name: upper, url: `/airports/${lower}` },
          { name: "Terminals", url: `/airports/${lower}/terminals` },
          { name: group.label, url: `/airports/${lower}/terminals/${group.slug}` },
        ])}
      />
      <SchemaMarkup schema={generateFAQSchema(faqs)} />

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 pt-4 pb-12">
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Airports", href: "/airports" },
              { label: upper, href: `/airports/${lower}` },
              { label: "Terminals", href: `/airports/${lower}/terminals` },
              { label: group.label, href: `/airports/${lower}/terminals/${group.slug}` },
            ]}
          />

          <div className="max-w-3xl mx-auto space-y-6">
            <header className="space-y-2 mt-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                {upper} {group.label}
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">
                {airport.name} · {cityName}
              </p>
            </header>

            {/* Live wait first — the reason to choose this result */}
            <LiveWaitStrip airportCode={upper} />

            {/* Who flies from here */}
            <Card className="rounded-xl shadow-md border-primary/30">
              <CardContent className="py-6 space-y-3">
                <h2 className="text-xl font-bold text-foreground">
                  Airlines at {upper} {group.label}
                </h2>
                <ul className="divide-y">
                  {group.airlines.map((a) => (
                    <li key={a.slug}>
                      <Link
                        to={`/airports/${lower}/terminals/${a.slug}`}
                        className="flex items-center justify-between gap-3 py-3 min-h-[44px] active:scale-95 transition"
                      >
                        <span className="text-base font-medium text-foreground">{a.airline}</span>
                        {a.concourse ? (
                          <Badge variant="secondary" className="shrink-0">{a.concourse}</Badge>
                        ) : null}
                      </Link>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground border-t pt-3">
                  Terminal assignments change. Confirm on your boarding pass or at{" "}
                  <a
                    href={info.officialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline inline-flex items-center gap-1"
                  >
                    the official {upper} site
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </a>
                  .
                </p>
              </CardContent>
            </Card>

            {/* Security detail */}
            {details && (
              <Card className="rounded-xl shadow-md">
                <CardContent className="py-6 space-y-3">
                  <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-primary shrink-0" aria-hidden="true" />
                    Security at {upper} {group.label}
                  </h2>
                  <p className="text-base text-muted-foreground">
                    Typical domestic wait at {upper} is {details.avgDomesticWait}; international
                    departures average {details.avgInternationalWait}.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant={details.preCheckLanes ? "default" : "secondary"}>
                      {details.preCheckLanes ? "TSA PreCheck available" : "No PreCheck lane"}
                    </Badge>
                    <Badge variant={details.clearLanes ? "default" : "secondary"}>
                      {details.clearLanes ? "CLEAR available" : "No CLEAR lane"}
                    </Badge>
                  </div>
                  {details.tipsSecurity?.length ? (
                    <ul className="space-y-1 pt-1">
                      {details.tipsSecurity.slice(0, 3).map((tip) => (
                        <li key={tip} className="text-base text-muted-foreground">
                          • {tip}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </CardContent>
              </Card>
            )}

            <AppStoreCTA
              placement="airport_hero"
              context={upper}
              headline={`Flying out of ${upper} ${group.label}?`}
              subline={`Airport Buddy watches the ${upper} security line and tells you exactly when to leave. Free on iPhone.`}
            />

            {/* Food in this terminal */}
            {terminalVenues.length > 0 && (
              <Card className="rounded-xl shadow-md">
                <CardContent className="py-6 space-y-3">
                  <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                    <Utensils className="h-5 w-5 text-primary shrink-0" aria-hidden="true" />
                    Where to eat in {group.label}
                  </h2>
                  <ul className="divide-y">
                    {terminalVenues.slice(0, 8).map((v) => (
                      <li key={`${v.name}-${v.terminal}`} className="py-3">
                        <p className="text-base font-medium text-foreground">{v.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {v.cuisine} · {v.price} · {v.hours}
                        </p>
                      </li>
                    ))}
                  </ul>
                  <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95">
                    <Link to={`/airports/${lower}/dining`}>All {upper} restaurants</Link>
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Getting between terminals */}
            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-2">
                <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary shrink-0" aria-hidden="true" />
                  Getting to {group.label} from another terminal
                </h2>
                <p className="text-base text-muted-foreground">{info.connection}</p>
              </CardContent>
            </Card>

            {/* Other terminals */}
            {otherTerminals.length > 0 && (
              <Card className="rounded-xl shadow-md">
                <CardContent className="py-6 space-y-4">
                  <h2 className="text-xl font-bold text-foreground">Other terminals at {upper}</h2>
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {otherTerminals.map((g) => (
                      <li key={g.slug}>
                        <Link
                          to={`/airports/${lower}/terminals/${g.slug}`}
                          className="flex items-center justify-between gap-2 rounded-xl border px-4 py-3 min-h-[44px] text-base hover:bg-accent/50 active:scale-95 transition"
                        >
                          <span className="font-medium">{g.label}</span>
                          <Badge variant="secondary">{g.airlines.length} airlines</Badge>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* FAQ */}
            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-5">
                <h2 className="text-xl font-bold text-foreground">
                  {upper} {group.label}: common questions
                </h2>
                {faqs.map((f) => (
                  <div key={f.question} className="space-y-1">
                    <h3 className="text-base font-semibold text-foreground">{f.question}</h3>
                    <p className="text-base text-muted-foreground">{f.answer}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}`}>{upper} live wait times</Link>
              </Button>
              <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}/terminals`}>All {upper} terminals</Link>
              </Button>
              <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}/parking`}>{upper} parking</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TerminalDetail;
